import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * <RecentlySold limit={6} /> — horizontal strip of recently sold cars.
 * Used on the Landing and inside Marketplace when the user selects the Sold tab.
 */
export default function RecentlySold({ limit = 8, title = "Recently Sold", showTitle = true }) {
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get(`/listings/recently-sold?limit=${limit}`)
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, [limit]);

    if (!items.length) return null;

    return (
        <section className="max-w-6xl mx-auto px-5 py-10" data-testid="recently-sold">
            {showTitle && (
                <div className="flex items-baseline justify-between mb-5">
                    <h2
                        className="text-2xl sm:text-3xl text-white"
                        style={{ fontFamily: "'Alfa Slab One', serif", fontStyle: "italic" }}
                    >
                        {title}
                    </h2>
                    <Link
                        to="/marketplace?status=sold"
                        data-testid="recently-sold-see-all"
                        className="text-xs font-bold text-hellcat hover:underline uppercase tracking-widest"
                    >
                        See all →
                    </Link>
                </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.slice(0, limit).map((l) => (
                    <Link
                        key={l.listing_id}
                        to={`/listings/${l.listing_id}`}
                        data-testid={`sold-card-${l.listing_id}`}
                        className="rounded-2xl overflow-hidden block group"
                        style={{
                            background: "linear-gradient(160deg, rgba(20,20,20,0.7), rgba(20,20,20,0.4))",
                            border: "1px solid rgba(255,255,255,0.06)",
                        }}
                    >
                        <div className="aspect-[4/3] overflow-hidden relative">
                            {l.photos?.[0] ? (
                                <img
                                    src={l.photos[0]}
                                    alt={l.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300"
                                    style={{ filter: "grayscale(0.4) brightness(0.85)" }}
                                />
                            ) : (
                                <div className="w-full h-full bg-onyx-900" />
                            )}
                            <span
                                className="absolute top-2 left-2 text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full"
                                style={{ background: "rgba(0,0,0,0.7)", color: "#fff", border: "1px solid rgba(225,6,0,0.5)" }}
                            >
                                SOLD
                            </span>
                        </div>
                        <div className="p-3">
                            <div className="text-sm font-semibold text-white truncate">
                                {l.year} {l.model_name} {l.trim}
                            </div>
                            <div className="flex items-baseline justify-between mt-1">
                                <span className="text-lg font-bold" style={{ color: "#E10600" }}>
                                    ${(l.sold_price || l.price)?.toLocaleString()}
                                </span>
                                {l.days_on_market != null && (
                                    <span className="text-[11px] text-onyx-500 inline-flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {l.days_on_market}d
                                    </span>
                                )}
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}
